import React from 'react';
import { UserPlus, Users, Brain, Sparkles, Target, BookOpen } from 'lucide-react';
import { WizardData } from '../AIActivityWizard';

interface WelcomeStepProps {
  wizardData: WizardData;
  onStepComplete: (data: Partial<WizardData>) => void;
  onNext: () => void;
  onPrevious: () => void;
  setIsGenerating: (generating: boolean) => void;
}

export function WelcomeStep({ wizardData, onStepComplete }: WelcomeStepProps) {
  const handleModeSelect = (mode: 'new-child' | 'existing-child') => {
    onStepComplete({ mode });
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          Welcome to the AI Activity Wizard!
        </h3>
        <p className="text-gray-600 max-w-2xl mx-auto">
          This wizard will help you create personalized learning activities using AI. 
          Choose your path below to get started.
        </p>
      </div>

      {/* Mode Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* New Child Mode */}
        <div
          className={`p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
            wizardData.mode === 'new-child'
              ? 'border-blue-500 bg-blue-50 shadow-md'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => handleModeSelect('new-child')}
        >
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <UserPlus className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Create New Child + Activities
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                Perfect for getting started! We'll create a child profile and generate 
                a complete set of personalized activities to kickstart their learning journey.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Target className="w-4 h-4 mr-2 text-green-500" />
                  Set up child profile & preferences
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Sparkles className="w-4 h-4 mr-2 text-purple-500" />
                  Generate 5-15 custom activities
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <BookOpen className="w-4 h-4 mr-2 text-blue-500" />
                  Across multiple subjects
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Existing Child Mode */}
        <div
          className={`p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg ${
            wizardData.mode === 'existing-child'
              ? 'border-purple-500 bg-purple-50 shadow-md'
              : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => handleModeSelect('existing-child')}
        >
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Generate Activities for Existing Child
              </h4>
              <p className="text-gray-600 text-sm mb-4">
                Already have children set up? Use this mode to create a batch of 
                new activities based on their existing learning profile and progress.
              </p>
              <div className="space-y-2">
                <div className="flex items-center text-sm text-gray-600">
                  <Users className="w-4 h-4 mr-2 text-purple-500" />
                  Select from existing children
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Brain className="w-4 h-4 mr-2 text-blue-500" />
                  Use existing learning data
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Sparkles className="w-4 h-4 mr-2 text-green-500" />
                  Generate targeted activities
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
        <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">
          What makes this wizard special?
        </h4>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Brain className="w-6 h-6 text-blue-600" />
            </div>
            <h5 className="font-semibold text-gray-900 text-sm">AI-Powered</h5>
            <p className="text-gray-600 text-xs">
              Uses advanced AI to create truly personalized content
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Target className="w-6 h-6 text-purple-600" />
            </div>
            <h5 className="font-semibold text-gray-900 text-sm">Curriculum Aligned</h5>
            <p className="text-gray-600 text-xs">
              Activities match learning objectives and standards
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Sparkles className="w-6 h-6 text-green-600" />
            </div>
            <h5 className="font-semibold text-gray-900 text-sm">Engaging</h5>
            <p className="text-gray-600 text-xs">
              Designed to be fun and motivating for children
            </p>
          </div>
        </div>
      </div>

      {/* Selection Indicator */}
      {wizardData.mode && (
        <div className="text-center">
          <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
            <Target className="w-4 h-4 mr-2" />
            {wizardData.mode === 'new-child' ? 'New Child + Activities' : 'Existing Child Activities'} selected
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Click "Next" to continue with your selection
          </p>
        </div>
      )}
    </div>
  );
}